import { useState } from 'react';
import { AppSidebar } from './AppSidebar';
import { TravelerTabBar } from './TravelerTabBar';
import { HostTabBar, showsHostTabBar } from './HostTabBar';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useContentWidth, WIDTH_CLASSES } from '../../hooks/useContentWidth';

interface AppLayoutProps {
  children: React.ReactNode;
  hideSidebar?: boolean;
  /** Full-height screen with its own header (chat). Drops the mobile top bar
   *  and the centered width wrapper so the child can own the viewport. */
  fullBleed?: boolean;
  /** Show the traveler's mobile floating tab bar (replaces the hamburger
   *  header on mobile — traveler routes only). */
  showTabBar?: boolean;
}

export function AppLayout({ children, hideSidebar, fullBleed, showTabBar }: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { width } = useContentWidth();
  const { activeRole } = useAuth();
  const { pathname } = useLocation();

  if (hideSidebar) return <>{children}</>;

  // The bottom tab bar is a traveler-only affordance; never show it to hosts/admins
  // (e.g. on the shared /settings route).
  const tabBar = showTabBar && activeRole === 'traveler';
  // Hosts get their own bar on the top-level host screens (not inside a thread).
  const hostTabBar = activeRole === 'host' && showsHostTabBar(pathname);

  return (
    <div className="flex h-dvh bg-app-bg">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar — hidden on full-bleed screens that ship their own
            header, and for travelers, whose mobile nav is the floating bar */}
        {!fullBleed && !tabBar && !hostTabBar && (
          <header className="md:hidden bg-app-card border-b border-app-border flex items-center px-4 gap-3 sticky top-0 z-30 shrink-0 pt-safe px-safe">
            <div className="h-12 flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="text-app-muted hover:text-app-fg transition-colors -ml-2 p-2.5 min-w-11 min-h-11 flex items-center justify-center"
                aria-label="Open menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              </button>
              <span className="text-app-fg font-semibold text-sm tracking-tight">Sekretly</span>
            </div>
          </header>
        )}

        {fullBleed ? (
          <main className="flex-1 min-h-0 flex flex-col">{children}</main>
        ) : (
          <main className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${tabBar || hostTabBar ? 'pt-safe' : ''}`}>
            <div className={`${WIDTH_CLASSES[width]} mx-auto w-full`}>
              {children}
            </div>
          </main>
        )}

        {tabBar && <TravelerTabBar active="more" />}
        {hostTabBar && <HostTabBar />}
      </div>
    </div>
  );
}
